import { useState } from "react";
import ReferrerTableActions from "./referrerTableActions";
import ReferrerCard from "../ReferrerCard.tsx";
import { useIsMobile } from "@/hooks";

interface ReferrerItem {
  id: number;
  name: string;
  lead_source: string;
  mobile_no: string;
  email: string;
  created_by: string;
  created_at: string;
}

interface ReferrerTableProps {
  data: ReferrerItem[];
  refetch: () => void;
  onSort: (field: string) => void;
  sortField: string;
  sortOrder: "asc" | "desc";
}

const ReferrerTable = ({ data, refetch, onSort, sortField, sortOrder }: ReferrerTableProps) => {
  const isMobile = useIsMobile();
  const [showSidebar, setShowSidebar] = useState<boolean>(false);
  const [leadID, setLeadID] = useState<number | null>(null);
  const [referrerName, setReferrerName] = useState<string | null>(null);
  const [leadSource, setLeadSource] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [createdBy, setCreatedBy] = useState<string | null>(null);
  const [createdAt, setCreatedAt] = useState<string | null>(null);

  const handleDetailClick = (id: number) => {
    const referrer = data.find((item) => item.id === id);
    setLeadID(id);
    setReferrerName(referrer?.name ?? null);
    setLeadSource(referrer?.lead_source ?? null);
    setPhoneNumber(referrer?.mobile_no ?? null);
    setEmail(referrer?.email ?? null);
    setCreatedBy(referrer?.created_by ?? null);
    setCreatedAt(referrer?.created_at ?? null);
    setShowSidebar(true);
  };

  return (
    <>
      <div className="">
        {/* Mobile: Card View */}
        {isMobile ? (
          <div className="space-y-3">
            {data.length > 0 ? (
              data.map((item, index) => (
                <ReferrerCard
                  key={`card-${item.id}-${index}`}
                  data={item}
                  onClick={() => handleDetailClick(item.id)}
                  refetch={refetch}
                />
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                No referrers found
              </div>
            )}
          </div>
        ) : (
          /* Desktop: Table View */
          <table className="min-w-full bg-white border-b border-gray-200 table-fixed">
            <thead>
              <tr className="text-gray-400 border-t">
                <th className="p-4 border-b text-left w-[5%]">ID</th>
                <th className="p-4 border-b text-left w-[15%]">Name</th>
                <th className="p-4 border-b text-left w-[12%]">Lead Source</th>
                <th className="p-4 border-b text-left w-[12%]">Phone Number</th>
                <th className="p-4 border-b text-left w-[18%]">Email</th>
                <th className="p-4 border-b text-left w-[13%]">Created By</th>
                <th
                  className="p-4 border-b text-left w-[13%] cursor-pointer hover:bg-gray-50 select-none"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSort("created_at");
                  }}
                >
                  <div className="flex items-center gap-1">
                    Created At
                    {sortField === "created_at" && (
                      <span className="text-gray-600">
                        {sortOrder === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </div>
                </th>
                <th className="p-4 border-b text-left w-[2%]">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.length > 0 ? (
                data.map((item, index) => (
                  <tr
                    key={`row-${item.id}-${index}`}
                    className="hover:bg-[#F7F7F7] cursor-pointer"
                    onClick={() => handleDetailClick(item.id)}
                  >
                    <td className="p-4 border-b text-gray-500 text-left w-[5%]">{item.id}</td>
                    <td className="p-4 border-b font-bold text-left w-[15%]">{item.name}</td>
                    <td className="p-4 border-b text-left w-[12%]">{item.lead_source}</td>
                    <td className="p-4 border-b text-left w-[12%]">{item.mobile_no}</td>
                    <td className="p-4 border-b text-left w-[18%]">{item.email}</td>
                    <td className="p-4 border-b text-left w-[13%]">{item.created_by}</td>
                    <td className="p-4 border-b text-left w-[13%] font-bold">{item.created_at}</td>
                    <td
                      className="p-4 border-b text-center w-[2%]"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ReferrerTableActions
                        id={item.id}
                        name={item.name}
                        lead_source={item.lead_source}
                        mobile_no={item.mobile_no}
                        email={item.email}
                        refetch={refetch}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="text-center py-4 text-gray-500 border-b">
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

export default ReferrerTable;